/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { createAction } from '../../utils/redux-utils';
import {
    CreateTemplateRulesParams,
    TemplateRuleDto,
    TemplateRuleTaxNumbersSearchParams,
    TemplateRulesListDto,
    TemplateRulesListSearchParams,
} from '../api/alerts/alertsClient.typings';
import { Notification } from '../../utils/utils';
import { PageQueryParams } from '../api/apiCommon.typings';

export enum AlertTemplateRulesActionType {
    OPEN_ALERT_TEMPLATE_RULES_PAGE = 'Alert template rules/ open page',
    APPLY_PAGING = 'Alert template rules/ apply paging',
    APPLY_FILTER = 'Alert template rules/ apply filter',
    EXIT_ALERT_TEMPLATE_RULES_PAGE = 'Alert template rules/ exit page',
    FETCH_ALERT_TEMPLATE_RULES_SUCCESS = 'Alerts template rules/ fetch alert template rules success',
    ALERTS_ALERT_TEMPLATE_RULES_ERROR = 'Alerts template rules/ fetch alert template rules error',

    CLOSE_ALERT_TEMPLATE_RULE_INFO_MODAL = 'Alert template rule/ close rule info modal',
    FETCH_ALERT_TEMPLATE_RULE_UNIQUE_IDENTIFIERS_START = 'Alert template rule/ fetch rule unique identifier start',
    FETCH_ALERT_TEMPLATE_RULE_UNIQUE_IDENTIFIERS_SUCCESS = 'Alert template rule/ fetch rule unique identifier success',
    FETCH_ALERT_TEMPLATE_RULE_UNIQUE_IDENTIFIERS_ERROR = 'Alert template rule/ fetch rule unique identifier error',

    SELECT_ALERT_TEMPLATE_RULES = 'Alerts template rules/ select alert template rules',

    CREATE_ALERT_TEMPLATE_RULES_START = 'Alert template rules/ create rules start',
    CREATE_ALERT_TEMPLATE_RULES_SUCCESS = 'Alert template rules/ create rules success',
    CREATE_ALERT_TEMPLATE_RULES_ERROR = 'Alert template rules/ create rules error',

    EDIT_ALERT_TEMPLATE_RULE_START = 'Alert template rules/ edit rule start',
    EDIT_ALERT_TEMPLATE_RULE_SUCCESS = 'Alert template rules/ edit rule success',
    EDIT_ALERT_TEMPLATE_RULE_ERROR = 'Alert template rules/ edit rule error',

    DELETE_ALERT_TEMPLATE_RULES_START = 'Alert template rules/ delete rules start',
    DELETE_ALERT_TEMPLATE_RULES_SUCCESS = 'Alert template rules/ delete rules success',
    DELETE_ALERT_TEMPLATE_RULES_ERROR = 'Alert template rules/ delete rules error',

    DOWNLOAD_TEMPLATE_RULES_START = 'Alert template rules/ download rules as excel start',
    DOWNLOAD_TEMPLATE_RULES_SUCCESS = 'Alert template rules/ download rules as excel success',
    DOWNLOAD_TEMPLATE_RULES_ERROR = 'Alert template rules/ download rules as excel error',

    DOWNLOAD_SAMPLE_RULES_START = 'Alert template rules/ download sample as excel start',
    DOWNLOAD_SAMPLE_RULES_SUCCESS = 'Alert template rules/ download sample as excel success',
    DOWNLOAD_SAMPLE_RULES_ERROR = 'Alert template rules/ download sample as excel error',
}

export const openAlertTemplateRulesPageAction = () =>
    createAction(AlertTemplateRulesActionType.OPEN_ALERT_TEMPLATE_RULES_PAGE);

export const applyPagingActionAction = (params: PageQueryParams) =>
    createAction(AlertTemplateRulesActionType.APPLY_PAGING, params);

export const applyFilteringActionAction = (params: TemplateRulesListSearchParams) =>
    createAction(AlertTemplateRulesActionType.APPLY_FILTER, params);

export const fetchAlertTemplateRulesSuccessAction = (list: TemplateRulesListDto) =>
    createAction(AlertTemplateRulesActionType.FETCH_ALERT_TEMPLATE_RULES_SUCCESS, list);

export const fetchAlertTemplateRulesErrorAction = (notification: Notification) =>
    createAction(AlertTemplateRulesActionType.ALERTS_ALERT_TEMPLATE_RULES_ERROR, { notification });

export const fetchAlertTemplateRuleUniqueStartAction = (params: TemplateRuleTaxNumbersSearchParams) =>
    createAction(AlertTemplateRulesActionType.FETCH_ALERT_TEMPLATE_RULE_UNIQUE_IDENTIFIERS_START, params);

export const fetchAlertTemplateRuleUniqueSuccessAction = (list: string[]) =>
    createAction(AlertTemplateRulesActionType.FETCH_ALERT_TEMPLATE_RULE_UNIQUE_IDENTIFIERS_SUCCESS, list);

export const fetchAlertTemplateRuleUniqueErrorAction = (notification: Notification) =>
    createAction(AlertTemplateRulesActionType.FETCH_ALERT_TEMPLATE_RULE_UNIQUE_IDENTIFIERS_ERROR, { notification });

export const closeAlertTemplateRuleInfoModalAction = () =>
    createAction(AlertTemplateRulesActionType.CLOSE_ALERT_TEMPLATE_RULE_INFO_MODAL);

export const selectAlertTemplateRulesAction = (rules: TemplateRuleDto[]) =>
    createAction(AlertTemplateRulesActionType.SELECT_ALERT_TEMPLATE_RULES, rules);

export const exitAlertTemplateRulesPageAction = () =>
    createAction(AlertTemplateRulesActionType.EXIT_ALERT_TEMPLATE_RULES_PAGE);

export const createAlertTemplateRulesStartAction = (params: CreateTemplateRulesParams) =>
    createAction(AlertTemplateRulesActionType.CREATE_ALERT_TEMPLATE_RULES_START, params);

export const createAlertTemplateRulesSuccessAction = () =>
    createAction(AlertTemplateRulesActionType.CREATE_ALERT_TEMPLATE_RULES_SUCCESS);

export const createAlertTemplateRulesErrorAction = (notification: Notification) =>
    createAction(AlertTemplateRulesActionType.CREATE_ALERT_TEMPLATE_RULES_ERROR, { notification });

export const editAlertTemplateRuleStartAction = (rule: Partial<TemplateRuleDto>) =>
    createAction(AlertTemplateRulesActionType.EDIT_ALERT_TEMPLATE_RULE_START, rule);

export const editAlertTemplateRuleSuccessAction = () =>
    createAction(AlertTemplateRulesActionType.EDIT_ALERT_TEMPLATE_RULE_SUCCESS);

export const editAlertTemplateRuleErrorAction = (notification: Notification) =>
    createAction(AlertTemplateRulesActionType.EDIT_ALERT_TEMPLATE_RULE_ERROR, { notification });

export const deleteAlertTemplateRulesStartAction = (rulesIds: number[]) =>
    createAction(AlertTemplateRulesActionType.DELETE_ALERT_TEMPLATE_RULES_START, rulesIds);

export const deleteAlertTemplateRulesSuccessAction = () =>
    createAction(AlertTemplateRulesActionType.DELETE_ALERT_TEMPLATE_RULES_SUCCESS);

export const deleteAlertTemplateRulesErrorAction = (notification: Notification) =>
    createAction(AlertTemplateRulesActionType.DELETE_ALERT_TEMPLATE_RULES_ERROR, { notification });

export const downloadAlertTemplateRuleStartAction = (params: Partial<TemplateRulesListSearchParams>) =>
    createAction(AlertTemplateRulesActionType.DOWNLOAD_TEMPLATE_RULES_START, params);

export const downloadAlertTemplateRuleSuccessAction = () =>
    createAction(AlertTemplateRulesActionType.DOWNLOAD_TEMPLATE_RULES_SUCCESS);

export const downloadAlertTemplateRuleErrorAction = (notification: Notification) =>
    createAction(AlertTemplateRulesActionType.DOWNLOAD_TEMPLATE_RULES_ERROR, { notification });

export const downloadSampleRulesStartAction = () =>
    createAction(AlertTemplateRulesActionType.DOWNLOAD_SAMPLE_RULES_START);

export const downloadSampleRulesSuccessAction = () =>
    createAction(AlertTemplateRulesActionType.DOWNLOAD_SAMPLE_RULES_SUCCESS);

export const downloadSampleRulesErrorAction = (notification: Notification) =>
    createAction(AlertTemplateRulesActionType.DOWNLOAD_SAMPLE_RULES_ERROR, { notification });

export type AlertTemplateRulesAction =
    | ReturnType<typeof openAlertTemplateRulesPageAction>
    | ReturnType<typeof applyPagingActionAction>
    | ReturnType<typeof applyFilteringActionAction>
    | ReturnType<typeof exitAlertTemplateRulesPageAction>
    | ReturnType<typeof fetchAlertTemplateRulesSuccessAction>
    | ReturnType<typeof fetchAlertTemplateRulesErrorAction>
    | ReturnType<typeof closeAlertTemplateRuleInfoModalAction>
    | ReturnType<typeof fetchAlertTemplateRuleUniqueStartAction>
    | ReturnType<typeof fetchAlertTemplateRuleUniqueSuccessAction>
    | ReturnType<typeof fetchAlertTemplateRuleUniqueErrorAction>
    | ReturnType<typeof createAlertTemplateRulesStartAction>
    | ReturnType<typeof createAlertTemplateRulesSuccessAction>
    | ReturnType<typeof createAlertTemplateRulesErrorAction>
    | ReturnType<typeof selectAlertTemplateRulesAction>
    | ReturnType<typeof editAlertTemplateRuleStartAction>
    | ReturnType<typeof editAlertTemplateRuleSuccessAction>
    | ReturnType<typeof editAlertTemplateRuleErrorAction>
    | ReturnType<typeof deleteAlertTemplateRulesStartAction>
    | ReturnType<typeof deleteAlertTemplateRulesSuccessAction>
    | ReturnType<typeof deleteAlertTemplateRulesErrorAction>
    | ReturnType<typeof downloadAlertTemplateRuleStartAction>
    | ReturnType<typeof downloadAlertTemplateRuleSuccessAction>
    | ReturnType<typeof downloadAlertTemplateRuleErrorAction>
    | ReturnType<typeof downloadSampleRulesStartAction>
    | ReturnType<typeof downloadSampleRulesSuccessAction>
    | ReturnType<typeof downloadSampleRulesErrorAction>;
